import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Undo2, Redo2, Save, Download, Eye, EyeOff,
  CheckCircle, AlertTriangle, Code2, Smartphone, Monitor,
  Play, ChevronDown, Zap, Loader2, Clock, Sun, Moon,
  Globe, ExternalLink,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useEditorStore } from '../../store/editorStore';
import { storiesApi } from '../../api/client';
import { useTheme } from '../../context/ThemeContext';
import ConfirmDialog from '../ConfirmDialog';

const PLAYER_URL = process.env.REACT_APP_PLAYER_URL || '';

export default function Toolbar() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const story = useEditorStore((s) => s.story);
  const isDirty = useEditorStore((s) => s.isDirty);
  const isSaving = useEditorStore((s) => s.isSaving);
  const lastSavedAt = useEditorStore((s) => s.lastSavedAt);
  const previewMode = useEditorStore((s) => s.previewMode);
  const showValidationPanel = useEditorStore((s) => s.showValidationPanel);
  const showAmpCodePanel = useEditorStore((s) => s.showAmpCodePanel);
  const validationResult = useEditorStore((s) => s.validationResult);
  const historyIndex = useEditorStore((s) => s.historyIndex);
  const history = useEditorStore((s) => s.history);

  const saveStory = useEditorStore((s) => s.saveStory);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  const setPreviewMode = useEditorStore((s) => s.setPreviewMode);
  const toggleValidationPanel = useEditorStore((s) => s.toggleValidationPanel);
  const toggleAmpCodePanel = useEditorStore((s) => s.toggleAmpCodePanel);
  const validateStory = useEditorStore((s) => s.validateStory);

  const updateStoryMetadata = useEditorStore((s) => s.updateStoryMetadata);

  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [confirmLeave, setConfirmLeave] = useState(false);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  async function handleSave() {
    await saveStory();
    toast.success('Story saved!', { id: 'save' });
  }

  async function handleSaveAndClose() {
    await saveStory();
    navigate('/marketing/story-builder');
  }

  async function handleValidate() {
    await validateStory();
    toggleValidationPanel();
    toast.success('Validation complete', { id: 'validate' });
  }

  async function handlePublish() {
    if (!story) return;
    setIsPublishing(true);
    try {
      await saveStory();
      const result = await storiesApi.publish(story.id);
      const publishedUrl = result?.cloudflareUrl || result?.publishedUrl || `${PLAYER_URL}/${story.id}`;
      toast.success(
        <div className="flex items-center gap-3">
          <div>
            <p className="font-semibold text-sm">Story published!</p>
            <p className="text-xs opacity-70">Ready to share</p>
          </div>
          <button
            onClick={() => window.open(publishedUrl, '_blank')}
            className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold"
            style={{ background: 'var(--brand-primary)', color: '#fff' }}
          >
            View Story
          </button>
        </div>,
        { duration: 8000 }
      );
    } catch {
      toast.error('Publish failed. Try saving first.');
    } finally {
      setIsPublishing(false);
    }
  }

  async function handleExport(format) {
    if (!story) return;
    setIsExporting(true);
    setShowExportMenu(false);
    try {
      const result = await storiesApi.export(story.id, { format, minify: false });
      if (format === 'zip') {
        window.open(result.downloadUrl, '_blank');
        toast.success('ZIP export ready!');
      } else if (result.html) {
        // Download as HTML file
        const blob = new Blob([result.html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${story?.metadata?.title || 'story'}.html`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('AMP HTML exported!');
      }
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setIsExporting(false);
    }
  }

  function formatSavedAt() {
    if (!lastSavedAt) return null;
    const d = new Date(lastSavedAt);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }

  const previewModes = [
    { mode: 'edit', icon: <EyeOff size={13} />, label: 'Edit' },
    { mode: 'mobile', icon: <Smartphone size={13} />, label: 'Mobile' },
    { mode: 'desktop', icon: <Monitor size={13} />, label: 'Desktop' },
    { mode: 'autoplay', icon: <Play size={13} />, label: 'Autoplay' },
  ];

  return (
    <header className="h-12 flex items-center px-3 gap-2 flex-shrink-0 z-20"
            style={{ background: 'var(--t-surface)', borderBottom: '1px solid var(--t-border)', fontFamily: 'Inter, sans-serif' }}>
      {/* Back to dashboard */}
      <button
        onClick={() => {
          if (isDirty) {
            setConfirmLeave(true);
          } else {
            navigate('/marketing/story-builder');
          }
        }}
        className="icon-btn"
        title="Back to Dashboard"
      >
        <ArrowLeft size={16} />
      </button>

      <div className="w-px h-6" style={{ background: 'var(--t-border)' }} />

      {/* Story title — click to edit */}
      <div className="flex items-center gap-2 min-w-0 mr-2">
        <div className="w-5 h-5 bg-indigo-600 rounded flex items-center justify-center flex-shrink-0">
          <Zap size={11} className="text-white" />
        </div>
        {isEditingTitle ? (
          <input
            className="bg-transparent border-b border-indigo-500 outline-none max-w-[220px] px-0.5"
            style={{ color: 'var(--t-text)', fontSize: '13px', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={() => {
              const trimmed = editTitle.trim();
              if (trimmed && trimmed !== story?.metadata?.title) {
                updateStoryMetadata({ title: trimmed });
              }
              setIsEditingTitle(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.target.blur();
              if (e.key === 'Escape') { setIsEditingTitle(false); }
            }}
            autoFocus
          />
        ) : (
          <span
            className="truncate max-w-[180px] cursor-pointer hover:text-indigo-400 transition-colors"
            style={{ color: 'var(--t-text)', fontSize: '13px', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}
            onClick={(e) => { e.stopPropagation(); setEditTitle(story?.metadata?.title || ''); setIsEditingTitle(true); }}
            title="Click to rename"
          >
            {story?.metadata?.title || 'Untitled Story'}
          </span>
        )}
        {isDirty && <span className="text-yellow-500 text-xs">•</span>}
      </div>

      <div className="w-px h-6" style={{ background: 'var(--t-border)' }} />

      {/* Undo / Redo */}
      <button
        onClick={undo}
        disabled={!canUndo}
        className="icon-btn disabled:opacity-30"
        title="Undo (⌘Z)"
      >
        <Undo2 size={15} />
      </button>
      <button
        onClick={redo}
        disabled={!canRedo}
        className="icon-btn disabled:opacity-30"
        title="Redo (⌘⇧Z)"
      >
        <Redo2 size={15} />
      </button>

      <div className="w-px h-6" style={{ background: 'var(--t-border)' }} />

      {/* Preview mode toggle */}
      <div className="flex items-center rounded-md p-0.5 gap-0.5"
           style={{ background: 'var(--t-panel)', border: '1px solid var(--t-border)' }}>
        {previewModes.map(({ mode, icon, label }) => (
          <button
            key={mode}
            onClick={() => setPreviewMode(mode)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded transition-colors
              ${previewMode === mode ? 'bg-indigo-600 text-white' : ''}`}
            style={{
              fontSize: '11.5px',
              fontWeight: previewMode === mode ? 600 : 400,
              fontFamily: 'Inter, sans-serif',
              ...(previewMode !== mode ? { color: 'var(--t-muted)' } : {}),
            }}
            title={label}
          >
            {icon}
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Save status */}
      {lastSavedAt && !isDirty && (
        <div className="flex items-center gap-1" style={{ color: 'var(--t-muted)', fontSize: '11.5px', fontFamily: 'Inter, sans-serif' }}>
          <Clock size={11} />
          <span>Saved {formatSavedAt()}</span>
        </div>
      )}

      {/* Theme toggle */}
      <button onClick={toggleTheme} className="icon-btn" title={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
        {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
      </button>

      {/* Validate */}
      <button
        onClick={handleValidate}
        className={`icon-btn relative ${showValidationPanel ? 'text-indigo-400' : ''}`}
        title="Validate Story"
      >
        {validationResult && validationResult.errorCount > 0 ? (
          <AlertTriangle size={16} className="text-yellow-500" />
        ) : validationResult && validationResult.valid ? (
          <CheckCircle size={16} className="text-green-500" />
        ) : (
          <CheckCircle size={16} />
        )}
        {validationResult && validationResult.errorCount > 0 && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 text-white text-[9px]
                           font-bold rounded-full flex items-center justify-center">
            {validationResult.errorCount}
          </span>
        )}
      </button>

      {/* AMP Code preview */}
      <button
        onClick={toggleAmpCodePanel}
        className={`icon-btn ${showAmpCodePanel ? 'text-indigo-400' : ''}`}
        title="View AMP Code"
      >
        <Code2 size={16} />
      </button>

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={isSaving}
        className="btn-secondary flex items-center gap-1.5"
        title="Save (⌘S)"
      >
        {isSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
        <span>Save</span>
      </button>

      {/* Save & Close */}
      <button
        onClick={handleSaveAndClose}
        disabled={isSaving}
        className="btn-secondary flex items-center gap-1.5"
        title="Save and go back to dashboard"
      >
        {isSaving ? <Loader2 size={13} className="animate-spin" /> : <ArrowLeft size={13} />}
        <span>Save & Close</span>
      </button>

      {/* Play story */}
      <button
        onClick={() => story && window.open(`/marketing/story-builder/${story.id}/play`, '_blank')}
        className="icon-btn"
        title="Play Story"
      >
        <Play size={15} />
      </button>

      {/* Publish */}
      <button
        onClick={handlePublish}
        disabled={isPublishing}
        className="flex items-center gap-1.5 px-3 py-1 rounded-md font-semibold transition-all"
        style={{
          background: story?.status === 'published'
            ? 'linear-gradient(135deg, #10b981, #059669)'
            : 'linear-gradient(135deg, var(--brand-primary), #8b5cf6)',
          color: '#fff',
          opacity: isPublishing ? 0.7 : 1,
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          letterSpacing: '-0.1px',
        }}
        title="Publish story"
      >
        {isPublishing ? <Loader2 size={12} className="animate-spin" /> : <Globe size={12} />}
        {story?.status === 'published' ? 'Re-publish' : 'Publish'}
      </button>

      {/* Preview link (published) or Export (draft) */}
      {story?.status === 'published' ? (
        <a
          href={story?.storyMetadata?.cloudflareUrl || story?.storyMetadata?.publishedUrl || `${PLAYER_URL}/${story?.id}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1.5 btn-primary"
        >
          <ExternalLink size={13} />
          <span>Preview</span>
        </a>
      ) : (
        <div className="relative">
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            disabled={isExporting}
            className="flex items-center gap-1 btn-primary"
          >
            {isExporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            <span>Export</span>
            <ChevronDown size={12} />
          </button>
          {showExportMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
              <div className="absolute right-0 top-full mt-1 rounded-lg shadow-xl z-50 min-w-[180px] py-1"
                style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
                <button onClick={() => handleExport('amp-html')} className="context-menu-item w-full text-left">
                  <Code2 size={14} className="text-indigo-500" />
                  Export AMP HTML
                </button>
                <button onClick={() => handleExport('zip')} className="context-menu-item w-full text-left">
                  <Download size={14} className="text-green-500" />
                  Download ZIP Package
                </button>
                <div className="context-menu-separator" />
                <button
                  onClick={() => {
                    if (story) {
                      const json = JSON.stringify(story, null, 2);
                      const blob = new Blob([json], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'story.json';
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('JSON exported!');
                    }
                    setShowExportMenu(false);
                  }}
                  className="context-menu-item w-full text-left"
                >
                  <Eye size={14} className="text-blue-500" />
                  Export Builder JSON
                </button>
              </div>
            </>
          )}
        </div>
      )}
      <ConfirmDialog
        isOpen={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        onConfirm={() => navigate('/marketing/story-builder')}
        title="Leave without saving?"
        message="You have unsaved changes. They will be lost if you leave now."
        confirmLabel="Leave"
        colorScheme="orange"
      />
    </header>
  );
}
